import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../Context/AuthProvider";
import { useTradeBlotterOrderStore } from "../../Store/tradeBlotterOrderStore";
import BondDetailsPanel from "../OrderEntry/BondDetailsPanel";
import TradeEntryDetails from "./TradeEntryDetails";
import useSnackbar from "../../Utils/useSnackbar";
import useBondCalculations from "../../hooks/useBondCalculations";
import { tokens, withAlpha } from "../../Common/designTokens"; // added

const TradeEntryForm = ({
  clearModalError,
  autoCalculateOnMount = false,
  lockedFields = [],
}) => {
  const {
    tradeEntryData,
    setTradeEntryData,
    bondDetails,
    cusipError,
    isEntryLoading,
    clearCalcTimestamp,
  } = useTradeBlotterOrderStore();

  const { token: jwtToken, userId } = useAuth();
  const decodedToken = jwtDecode(jwtToken);

  const { SnackbarComponent } = useSnackbar();

  const [maturityMessage, setMaturityMessage] = useState(null);
  // Trigger to force re-run of price-dependent effect when manual calc invoked
  const [priceRecalcTrigger, setPriceRecalcTrigger] = useState(0);

  // Handle settlement date from bond calculations
  const handleSettlementDateReceived = useCallback((settlementDate) => {
    setTradeEntryData({ settlementDate });
  }, [setTradeEntryData]);

  const {
    setBondCalcResults,
    bondCalcResults,
    calcLoading,
    calcErrorMessage,
    calculateBondValues,
  } = useBondCalculations({
    cusip: tradeEntryData.cusip,
    price: tradeEntryData.tradePrice,
    userId: userId || decodedToken.userId,
    token: jwtToken,
    onSettlementDateReceived: handleSettlementDateReceived
  });

  const isSecMasterMatured = (secMasterOrData) => {
    if (!secMasterOrData) return false;
    const sec = secMasterOrData.secMasterDTO || secMasterOrData;
    const maturityStr =
      (sec && (sec.maturityDate || sec.maturity)) ||
      (secMasterOrData && secMasterOrData.maturity);
    if (!maturityStr) return false;
    const maturityDate = new Date(`${maturityStr}T00:00:00Z`);
    if (Number.isNaN(maturityDate.getTime())) return false;

    const todayUtcMidnight = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00Z");
    return maturityDate.getTime() < todayUtcMidnight.getTime();
  };

  // Remove previous handlePriceBlur wrapper and just use callBondCalcManually
  const callBondCalcManually = useCallback(() => {
    if (
      bondDetails &&
      tradeEntryData.cusip &&
      tradeEntryData.cusip.length >= 9 &&
      tradeEntryData.tradePrice
    ) {
      //somehow trigger here useffect that has tradeprice dependency
      if (isSecMasterMatured(bondDetails)) {
        setMaturityMessage(
          "This bond has already matured (maturity date is before today). Bond calculations are not available."
        );
        setBondCalcResults(null);
        return;
      }
      setMaturityMessage(null);
      calculateBondValues(
        tradeEntryData.tradePrice,
        tradeEntryData.cusip,
        tradeEntryData.executionDateTime
      );
      // force the dependent effect to run after manual calc
      setPriceRecalcTrigger(t => t + 1);
    }
  }, [
    bondDetails,
    tradeEntryData.cusip,
    tradeEntryData.tradePrice,
    tradeEntryData.executionDateTime,
    calculateBondValues,
    setBondCalcResults,
    setPriceRecalcTrigger
  ]);

  // Handle execution date time confirmation (when OK is clicked)
  const handleExecutionDateTimeAccept = useCallback((newValue) => {
    if (
      bondDetails && 
      tradeEntryData.cusip && 
      tradeEntryData.cusip.length >= 9 && 
      tradeEntryData.tradePrice &&
      newValue
    ) {
      if (isSecMasterMatured(bondDetails)) {
        setMaturityMessage(
          "This bond has already matured (maturity date is before today). Bond calculations are not available."
        );
        setBondCalcResults(null);
        return;
      }
      setMaturityMessage(null);
      const executionDateTime = newValue.format("YYYY-MM-DDTHH:mm");
      calculateBondValues(tradeEntryData.tradePrice, tradeEntryData.cusip, executionDateTime);
      // ensure recalculation effect runs after this manual calc as well
      setPriceRecalcTrigger(t => t + 1);
    }
  }, [bondDetails, tradeEntryData.cusip, tradeEntryData.tradePrice, calculateBondValues, setBondCalcResults, setPriceRecalcTrigger]);

  // Auto-recalculate principalAmount, accruedInterest, estimatedTotal when dependencies change.
  useEffect(() => {
    const parseField = (v) => {
      if (v === '' || v === null || v === undefined) return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };

    const rawQty = tradeEntryData.tradeQuantity !== undefined ? tradeEntryData.tradeQuantity : tradeEntryData.qty;
    const rawPrice = tradeEntryData.tradePrice !== undefined ? tradeEntryData.tradePrice : tradeEntryData.price;

    const qty = parseField(rawQty);
    const price = parseField(rawPrice);
    const accrPerUnit = parseField(bondCalcResults?.Accr_Interest);

    const qtyPresent = Number.isFinite(qty);
    const pricePresent = Number.isFinite(price);
    const accrPresent = Number.isFinite(accrPerUnit);

    const toMoney = (n) => n.toFixed(2);
    const updates = {};
    let changed = false;

    if (!qtyPresent) {
      if (tradeEntryData.principalAmount !== '') { updates.principalAmount = ''; changed = true; }
      if (tradeEntryData.accruedInterest !== '') { updates.accruedInterest = ''; changed = true; }
      if (tradeEntryData.estimatedTotal !== '') { updates.estimatedTotal = ''; changed = true; }
      if (changed) setTradeEntryData(updates);
      return;
    }

    if (accrPresent) {
      const accrued = (accrPerUnit * qty) / 100;
      const currentAccrued = Number(tradeEntryData.accruedInterest);
      if (!Number.isFinite(currentAccrued) || Math.abs(currentAccrued - accrued) > 0.005) {
        updates.accruedInterest = toMoney(accrued);
        changed = true;
      }
    }

    if (pricePresent) {
      const principal = (price * qty) / 100;
      const currentPrincipal = Number(tradeEntryData.principalAmount);
      if (!Number.isFinite(currentPrincipal) || Math.abs(currentPrincipal - principal) > 0.005) {
        updates.principalAmount = toMoney(principal);
        changed = true;
      }

      const accruedNum = Number(updates.accruedInterest ?? tradeEntryData.accruedInterest);
      const accruedForTotal = Number.isFinite(accruedNum) ? accruedNum : 0;
      const estimated = principal + accruedForTotal;
      const currentEstimated = Number(tradeEntryData.estimatedTotal);
      if (!Number.isFinite(currentEstimated) || Math.abs(currentEstimated - estimated) > 0.005) {
        updates.estimatedTotal = toMoney(estimated);
        changed = true;
      }
    } else {
      if (tradeEntryData.principalAmount !== '') { updates.principalAmount = ''; changed = true; }
      if (tradeEntryData.estimatedTotal !== '') { updates.estimatedTotal = ''; changed = true; }
    }

    if (changed) setTradeEntryData(updates);
  }, [
    tradeEntryData.tradeQuantity,
    tradeEntryData.qty,
    tradeEntryData.tradePrice,
    tradeEntryData.price,
    bondCalcResults?.Accr_Interest,
    // include trigger so manual calc forces this effect
    priceRecalcTrigger,
  ]);

  useEffect(() => {
    if (clearCalcTimestamp) {
      setBondCalcResults(null);
      setMaturityMessage(null);
    }
  }, [clearCalcTimestamp, setBondCalcResults]);

  useEffect(() => {
    if (
      autoCalculateOnMount &&
      tradeEntryData.cusip &&
      tradeEntryData.tradePrice
    ) {
      callBondCalcManually();
    }
  }, [
    autoCalculateOnMount,
    tradeEntryData.cusip,
    tradeEntryData.tradePrice,
    bondDetails,
    callBondCalcManually
  ]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <SnackbarComponent />

        <TradeEntryDetails 
          clearModalError={clearModalError}
          onPriceBlur={callBondCalcManually}
          calcLoading={calcLoading}
          onExecutionDateTimeAccept={handleExecutionDateTimeAccept}
          lockedFields={lockedFields}
        />

        {(bondDetails || bondCalcResults || calcLoading || calcErrorMessage || maturityMessage || cusipError) && (
          <Box sx={{ mt: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: tokens.colors.fontColour,
                mb: 2,
                fontSize: '1.1rem',
                borderBottom: `2px solid ${tokens.colors.bodyBorderColor50}`,
                paddingBottom: '8px'
              }}
            >
            Bond Details
            </Typography>
            <BondDetailsPanel
              bondDetails={bondDetails}
              bondCalcResults={bondCalcResults}
              calcLoading={calcLoading}
              cusipError={cusipError}
              isLoading={isEntryLoading}
              actualOrderData={tradeEntryData}
              calcErrorMessage={calcErrorMessage || maturityMessage}
              isTradeEntry={true}
            />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default TradeEntryForm;